import React from 'react';
import { Switch } from '../../ui/switch';

/**
 * Piezas comunes de WhatsApp → Bot (1/11/2026) y de los borradores del bot en
 * la bandeja: la tarjeta de cada sección, las pastillas del estado que
 * devuelve el bot (clasificación, intención, fase, traspaso, alertas,
 * puntuación) con el resumen para el equipo plegable, y los formatos.
 *
 * Aquí no hay ningún dato de la clínica: solo los nombres en castellano de
 * los valores fijos del esquema de salida del bot.
 */

export const BOTON_VERDE = 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white';
export const FOCO = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E] focus-visible:ring-offset-1';

/** Hay clave y el servidor la puede leer (si no, el bot no puede llamar a Claude). */
export function claveUsable(bot) {
  return !!(bot && bot.clave_puesta && !bot.clave_ilegible);
}

/** La respuesta de la API solo sirve si es un objeto (no una lista ni nada). */
export function comoObjeto(d) {
  return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
}

export function numero(n) {
  return Number(n || 0).toLocaleString('es-ES');
}

/** Fecha y hora locales, cortas: «1/11/2026, 18:40». */
export function cuando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-ES', {
    day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** «45 s», «1 min 20 s», «2 h 5 min»; sin dato, «—». */
export function duracion(s) {
  if (s === null || s === undefined || s === '' || Number.isNaN(Number(s))) return '—';
  const t = Math.round(Number(s));
  if (t < 60) return `${t} s`;
  const min = Math.floor(t / 60);
  const seg = t % 60;
  if (min < 60) return seg ? `${min} min ${seg} s` : `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Euros; por debajo de un euro, con tres decimales (el coste por respuesta es de céntimos). */
export function euros(v) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const dec = n !== 0 && Math.abs(n) < 1 ? 3 : 2;
  return n.toLocaleString('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: dec,
  });
}

/** «a», «a y b», «a, b y c». */
export function unir(partes) {
  const p = partes.filter(Boolean);
  if (p.length <= 1) return p.join('');
  return `${p.slice(0, -1).join(', ')} y ${p[p.length - 1]}`;
}

/** Una sección numerada de la pantalla del bot. */
export function Seccion({ id, n, titulo, descripcion, accion, children }) {
  return (
    <section id={`bot-${id}`} aria-labelledby={`bot-${id}-titulo`}
      className="cjo-card p-4 sm:p-5 scroll-mt-4 min-w-0" data-testid={`bot-seccion-${id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h2 id={`bot-${id}-titulo`} className="font-display font-semibold text-[#13231E] text-base flex items-center gap-2">
            <span aria-hidden="true"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(46,110,94,0.12)] text-[12px] font-semibold text-[#2E6E5E]">
              {n}
            </span>
            {titulo}
          </h2>
          {descripcion ? <p className="text-xs text-muted-foreground mt-1 max-w-3xl">{descripcion}</p> : null}
        </div>
        {accion}
      </div>
      {children}
    </section>
  );
}

/** Un grupo de pastillas que se comporta como botones de opción. */
export function Pildoras({ etiqueta, opciones, valor, onCambio, testid, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={etiqueta} data-testid={testid}>
      {opciones.map(([clave, nombre]) => (
        <button key={clave} type="button" role="radio" aria-checked={valor === clave} disabled={disabled}
          onClick={() => onCambio(clave)} data-testid={testid ? `${testid}-${clave}` : undefined}
          className={`rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50 ${FOCO} ${
            valor === clave ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : 'hover:bg-muted/60'}`}>
          {nombre}
        </button>
      ))}
    </div>
  );
}

/** Un interruptor con su título y su explicación. */
export function Interruptor({ id, activo, onCambio, titulo, children, disabled, testid }) {
  return (
    <div className="flex items-start gap-3 py-1" data-testid={testid}>
      <Switch id={id} checked={!!activo} onCheckedChange={onCambio} disabled={disabled}
        className="mt-0.5 data-[state=checked]:bg-[#2E6E5E]" data-testid={testid ? `${testid}-interruptor` : undefined} />
      <div className="text-sm min-w-0">
        <label htmlFor={id} className="font-medium text-[#13231E] cursor-pointer">{titulo}</label>
        {children ? <div className="text-xs text-muted-foreground">{children}</div> : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ estado

const CLASIFICACION = {
  caliente: ['Caliente', 'bg-red-100 text-red-800 ring-red-200'],
  templado: ['Templado', 'bg-amber-100 text-amber-900 ring-amber-200'],
  frio: ['Frío', 'bg-sky-100 text-sky-800 ring-sky-200'],
  descartar: ['Descartar', 'bg-zinc-200 text-zinc-700 ring-zinc-300'],
  pendiente: ['Pendiente', 'bg-slate-100 text-slate-700 ring-slate-200'],
};

/** Color de cada clasificación en la barra del reparto (el mismo tono que su pastilla). */
export const COLOR_BARRA = {
  caliente: 'bg-red-500',
  templado: 'bg-amber-400',
  frio: 'bg-sky-500',
  descartar: 'bg-zinc-400',
  pendiente: 'bg-slate-200',
};

export const ORDEN_CLASIFICACION = ['caliente', 'templado', 'frio', 'descartar', 'pendiente'];

const ETIQUETAS = {
  clasificacion: Object.fromEntries(Object.entries(CLASIFICACION).map(([k, [e]]) => [k, e])),
  intencion: {
    cita: 'Quiere cita',
    presupuesto: 'Pide presupuesto',
    informacion: 'Pide información',
    curiosea: 'Curiosea',
    desconocida: 'Intención sin saber',
  },
  fase: {
    apertura: 'apertura',
    preocupacion: 'preocupación',
    orientacion: 'orientación',
    precio: 'precio',
    valoracion: 'valoración',
    cierre: 'cierre',
    traspasado: 'traspasado',
    cerrado: 'cerrado',
  },
  modalidad: { presencial: 'Presencial', online: 'Online', desconocida: 'Sin saber' },
  reaccion_precio: {
    acepta: 'Lo acepta',
    duda: 'Duda',
    rechaza: 'Lo rechaza',
    sin_informar: 'Aún no se ha dado precio',
  },
  alertas: {
    urgencia: 'Urgencia',
    paciente_operado: 'Paciente operado',
    menor: 'Menor de edad',
    caso_complejo: 'Caso complejo',
    patologia_ocular: 'Patología ocular',
    fuera_de_alcance: 'Fuera de lo que se hace',
    pide_humano: 'Pide hablar con una persona',
    foto_recibida: 'Ha mandado una foto',
  },
};

/** El nombre en castellano de un valor del estado; si no se conoce, el valor tal cual. */
export function etiqueta(campo, valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  return ETIQUETAS[campo]?.[valor] || String(valor);
}

const ALERTA_GRAVE = ['urgencia', 'paciente_operado'];
const ALERTA_DESCARTE = ['menor', 'fuera_de_alcance'];

function Chip({ children, clase = 'bg-white text-[#13231E] ring-border', testid, valor, title }) {
  return (
    <span data-testid={testid} data-valor={valor} title={title}
      className={`inline-flex items-center rounded-full px-2 py-px text-[11px] font-semibold ring-1 ring-inset ${clase}`}>
      {children}
    </span>
  );
}

export function ChipClasificacion({ valor }) {
  if (!valor) return null;
  const [texto, clase] = CLASIFICACION[valor] || [String(valor), 'bg-slate-100 text-slate-700 ring-slate-200'];
  return <Chip clase={clase} testid="bot-chip-clasificacion" valor={valor}>{texto}</Chip>;
}

/** Las pastillas del estado: lo que ha entendido el bot de la conversación. */
export function ChipsEstado({ estado, testid = 'bot-chips' }) {
  const e = comoObjeto(estado);
  if (!Object.keys(e).length) return null;
  const alertas = Array.isArray(e.alertas) ? e.alertas : [];
  const puntuacion = e.puntuacion === null || e.puntuacion === undefined || e.puntuacion === ''
    ? null : e.puntuacion;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
      <ChipClasificacion valor={e.clasificacion} />
      {e.intencion ? <Chip testid="bot-chip-intencion" valor={e.intencion}>{etiqueta('intencion', e.intencion)}</Chip> : null}
      {e.fase ? <Chip testid="bot-chip-fase" valor={e.fase}>Fase: {etiqueta('fase', e.fase)}</Chip> : null}
      {e.traspaso === true ? (
        <Chip testid="bot-chip-traspaso" clase="bg-violet-100 text-violet-800 ring-violet-200">Pasa al equipo</Chip>
      ) : null}
      {alertas.map((a) => (
        <Chip key={a} testid="bot-chip-alerta" valor={a}
          clase={ALERTA_GRAVE.includes(a) ? 'bg-red-600 text-white ring-red-700'
            : ALERTA_DESCARTE.includes(a) ? 'bg-zinc-100 text-zinc-700 ring-zinc-300'
              : 'bg-amber-50 text-amber-900 ring-amber-300'}>
          {etiqueta('alertas', a)}
        </Chip>
      ))}
      {puntuacion !== null ? (
        <Chip testid="bot-chip-puntuacion" valor={String(puntuacion)} title="Puntuación que da el bot al lead">
          Puntuación {puntuacion}
        </Chip>
      ) : null}
    </div>
  );
}

/** El resumen para el equipo y el resto de lo que apunta el bot, plegado. */
export function DetalleEstado({ estado, testid = 'bot-detalle-estado' }) {
  const e = comoObjeto(estado);
  if (!Object.keys(e).length) return null;
  const filas = [
    ['Tratamiento recomendado', e.tratamiento_recomendado],
    ['Especialista', e.especialista],
    ['Qué le preocupa', e.preocupacion],
    ['Precio informado', e.precio_informado],
    ['Reacción al precio', etiqueta('reaccion_precio', e.reaccion_precio)],
    ['Plazo', e.plazo],
    ['Ciudad', e.ciudad],
    ['Modalidad', etiqueta('modalidad', e.modalidad)],
    ['Siguiente paso', e.siguiente_paso],
  ].filter(([, v]) => v);
  return (
    <details className="mt-1 text-xs" data-testid={testid}>
      <summary className={`cursor-pointer select-none font-medium text-[#2E6E5E] rounded ${FOCO}`}>
        Resumen para el equipo
      </summary>
      <p className="mt-1 whitespace-pre-wrap break-words text-[#13231E]" data-testid={`${testid}-resumen`}>
        {e.resumen_para_equipo || 'Sin resumen todavía.'}
      </p>
      {filas.length ? (
        <dl className="mt-1.5 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {filas.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-words text-[#13231E]">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
    </details>
  );
}

/** «2,3 s · tokens: 120 de entrada, 9.000 de caché, 210 de salida · modelo». */
export function lineaUso({ ms, uso, modelo } = {}) {
  const u = comoObjeto(uso);
  const partes = [];
  if (ms !== null && ms !== undefined && !Number.isNaN(Number(ms))) {
    partes.push(`${(Number(ms) / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })} s`);
  }
  const tokens = [];
  if (u.entrada !== undefined) tokens.push(`${numero(u.entrada)} de entrada`);
  if (u.cache_leidos) tokens.push(`${numero(u.cache_leidos)} leídos de caché`);
  if (u.cache_creados) tokens.push(`${numero(u.cache_creados)} escritos en caché`);
  if (u.salida !== undefined) tokens.push(`${numero(u.salida)} de salida`);
  if (tokens.length) partes.push(`tokens: ${tokens.join(', ')}`);
  if (modelo) partes.push(modelo);
  return partes.join(' · ');
}

/** Las pastillas, el resumen plegable y lo que costó: debajo de cada respuesta del bot. */
export function EstadoRespuesta({ estado, ms, uso, modelo, testid = 'bot-estado' }) {
  const linea = lineaUso({ ms, uso, modelo });
  return (
    <div className="space-y-0.5" data-testid={testid}>
      <ChipsEstado estado={estado} />
      <DetalleEstado estado={estado} />
      {linea ? <div className="text-[11px] text-muted-foreground tabular-nums" data-testid={`${testid}-uso`}>{linea}</div> : null}
    </div>
  );
}
